import * as Constants from "./Constants";

/**
 * Dashboard device-type tab order — default order plus a per-user, drag-and-drop
 * customised order persisted in localStorage (keyed by the logged-in userId so two
 * people sharing a browser each keep their own arrangement).
 *
 * Browser-only for now — it won't follow a user to another device. When a backend
 * user-preferences endpoint exists, load/save through it here and keep localStorage as
 * the offline fallback.
 */
export const DEFAULT_DEVICE_TAB_ORDER = ["GT06_TRACKER", "GPS_LOCK", "BT_LOCK", "MOBI_TRACK"];

const STORAGE_PREFIX = "dash_tab_order_";

/**
 * Reconcile a stored order against the known tab keys: drops unknown/duplicate keys and
 * appends any known key missing from the stored list (e.g. a tab added in a later release),
 * so a stale saved order can never hide a tab.
 */
export function normalizeTabOrder(stored, known = DEFAULT_DEVICE_TAB_ORDER) {
  const seen = new Set();
  const result = [];
  if (Array.isArray(stored)) {
    for (const key of stored) {
      if (known.includes(key) && !seen.has(key)) {
        seen.add(key);
        result.push(key);
      }
    }
  }
  for (const key of known) {
    if (!seen.has(key)) result.push(key);
  }
  return result;
}

/** Move `fromKey` to the position currently occupied by `toKey`. Returns a new array. */
export function moveTab(order, fromKey, toKey) {
  const from = order.indexOf(fromKey);
  const to = order.indexOf(toKey);
  if (from === -1 || to === -1 || from === to) return order;
  const next = order.slice();
  next.splice(from, 1);
  next.splice(to, 0, fromKey);
  return next;
}

function storageKey() {
  try {
    const user = JSON.parse(localStorage.getItem(Constants.LOGGED_IN_USER));
    return STORAGE_PREFIX + (user?.userId ?? "anon");
  } catch {
    return STORAGE_PREFIX + "anon";
  }
}

export function loadTabOrder() {
  try {
    return normalizeTabOrder(JSON.parse(localStorage.getItem(storageKey())));
  } catch {
    return DEFAULT_DEVICE_TAB_ORDER.slice();
  }
}

export function saveTabOrder(order) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(order));
  } catch {
    // Storage unavailable (private mode / quota) — order just won't persist.
  }
}

export function resetTabOrder() {
  try {
    localStorage.removeItem(storageKey());
  } catch {
    // ignore
  }
  return DEFAULT_DEVICE_TAB_ORDER.slice();
}
